import React, { useEffect, useMemo, useState } from "react";
import Shell from "../components/Shell";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Sparkline } from "../components/Sparkline";
import MonthPicker from "../components/MonthPicker";
import { toast } from "sonner";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const periods = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "year", label: "Year" },
];

const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Spider-verse palette
const PIE_COLORS = [
  "#DC1F26", // Spidey red
  "#1E3A8A", // Suit blue
  "#F59E0B", // Web gold
  "#7C3AED", // Symbiote purple
  "#059669", // Miles green
  "#EC4899", // Gwen pink
  "#0EA5E9", // Web-blue
  "#EF4444", // Alert red
  "#14B8A6", // Teal
  "#A855F7", // Violet
];

export default function Analytics() {
  const { user, checkAuth } = useAuth();
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projection, setProjection] = useState(null);
  const [lookback, setLookback] = useState(2);
  const [excludeOneoff, setExcludeOneoff] = useState(false);
  const [savingExclusion, setSavingExclusion] = useState(false);
  const [month, setMonth] = useState(currentMonthStr());

  const pinnedExcluded = useMemo(
    () => new Set(user?.excluded_projection_categories || []),
    [user],
  );

  useEffect(() => {
    const params = { period };
    if (month && month !== currentMonthStr()) params.month = month;
    api.get("/analytics/spending", { params }).then(r => setData(r.data));
  }, [period, month]);
  useEffect(() => {
    const params = month && month !== currentMonthStr() ? { month } : {};
    api.get("/analytics/summary", { params }).then(r => setSummary(r.data));
  }, [month]);
  useEffect(() => {
    api.get("/analytics/projection", {
      params: { months: lookback, exclude_oneoff: excludeOneoff },
    }).then(r => setProjection(r.data));
  }, [lookback, excludeOneoff, user?.excluded_projection_categories]);

  const togglePinnedCategory = async (cat) => {
    const current = new Set(pinnedExcluded);
    if (current.has(cat)) current.delete(cat); else current.add(cat);
    const next = Array.from(current);
    setSavingExclusion(true);
    try {
      await api.patch("/user/settings", { excluded_projection_categories: next });
      await checkAuth();
      toast.success(current.has(cat) ? `Ignored "${cat}" in projections` : `"${cat}" back in projections`);
    } catch (e) {
      toast.error("Couldn't update exclusions");
    } finally {
      setSavingExclusion(false);
    }
  };

  const cur = user?.currency || "USD";
  const cats = summary?.categories || [];
  const totalExp = summary?.month_expense || 1;

  const pieData = useMemo(() => cats.slice(0, 10).map(c => ({
    name: c.category, value: Number(c.amount.toFixed(2)),
  })), [cats]);

  const projCats = projection?.categories || [];
  const projTotal = projection?.total || 0;
  const monthsAnalyzed = projection?.months_analyzed ?? 0;
  const monthlyTotals = projection?.monthly_totals || [];
  const oneoffCount = (projection?.oneoff_categories || []).length;
  const pinnedCategoriesData = projection?.pinned_excluded_categories || [];
  const pinnedSavings = pinnedCategoriesData.reduce((s, x) => s + (x.amount || 0), 0);
  const monthlyBudget = projection?.monthly_budget || 0;
  const overBudget = monthlyBudget > 0 && projTotal > monthlyBudget;
  const overBy = Math.max(0, projTotal - monthlyBudget);
  const budgetPct = monthlyBudget > 0 ? Math.min(100, Math.round((projTotal / monthlyBudget) * 100)) : 0;
  const monthLabels = { 1: "Last month", 2: "Two months ago", 3: "Three months ago", 4: "Four months ago", 5: "Five months ago", 6: "Six months ago" };

  // Every known category: projected + oneoff + pinned + this-month summary
  const allKnownCategories = useMemo(() => {
    const s = new Set();
    (projection?.categories || []).forEach(c => s.add(c.category));
    (projection?.oneoff_categories || []).forEach(c => s.add(c.category));
    (projection?.pinned_excluded_categories || []).forEach(c => s.add(c.category));
    (summary?.categories || []).forEach(c => s.add(c.category));
    pinnedExcluded.forEach(c => s.add(c));
    return Array.from(s).sort();
  }, [projection, summary, pinnedExcluded]);

  return (
    <Shell>
      <div className="mb-4 sm:mb-6 flex items-start sm:items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">Spending Analysis</h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            {summary?.month_label ? `Viewing ${summary.month_label}` : "Understand where your money goes"}
          </p>
        </div>
        <MonthPicker value={month} onChange={setMonth} testId="analytics-month-picker" />
      </div>

      <div className="grid grid-cols-12 gap-4 sm:gap-6">
        <div className="col-span-12 lg:col-span-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-2">
            <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100">Spending Over Time</h3>
            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-full p-1">
              {periods.map(p => (
                <button
                  key={p.key}
                  data-testid={`period-${p.key}-btn`}
                  onClick={() => setPeriod(p.key)}
                  className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${period === p.key ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
                >{p.label}</button>
              ))}
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }} className="sm:!h-[300px]">
            <ResponsiveContainer>
              <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#94A3B8", fontSize: 11 }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: "#94A3B8", fontSize: 11 }} width={44} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
                  formatter={(v) => fmtMoney(v, cur)}
                />
                <Bar dataKey="amount" fill="#DC1F26" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6">
          <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">Top Categories</h3>
          {cats.length === 0 ? (
            <div className="text-sm text-slate-500 dark:text-slate-400 py-6">No expenses in {summary?.month_label || "this month"}.</div>
          ) : (
            <div className="space-y-5">
              {cats.slice(0, 6).map((c) => {
                const p = Math.round((c.amount / totalExp) * 100);
                return (
                  <div key={c.category}>
                    <div className="flex justify-between text-sm">
                      <div className="font-semibold text-slate-800 dark:text-slate-200">{c.category}</div>
                      <div className="tabular text-slate-500 dark:text-slate-400">{p}%</div>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 tabular mb-2">{fmtMoney(c.amount, cur)}</div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-spidey-red rounded-full" style={{ width: `${p}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Category Pie Chart */}
        <div
          data-testid="category-pie-card"
          className="col-span-12 lg:col-span-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100">Category Breakdown</h3>
            <span className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
              {summary?.month_label || "This Month"}
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Where every dollar swings in {summary?.month_label || "this month"}</p>
          {pieData.length === 0 ? (
            <div className="text-sm text-slate-500 dark:text-slate-400 py-16 text-center">No expenses tracked yet.</div>
          ) : (
            <div style={{ width: "100%", height: 280 }} className="sm:!h-[320px]" data-testid="category-pie-chart">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={110}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
                    formatter={(v, n) => [fmtMoney(v, cur), n]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    wrapperStyle={{ fontSize: 11 }}
                    iconType="circle"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Next Month Projection */}
        <div
          data-testid="projection-card"
          className="col-span-12 lg:col-span-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-6"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100">Next Month Projection</h3>
            <span className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-spidey-red">Spidey-Sense</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Estimate based on the average of your past {monthsAnalyzed === 0 ? "months" : `${monthsAnalyzed} month${monthsAnalyzed > 1 ? "s" : ""}`} of spending.
          </p>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-full p-1" data-testid="lookback-picker">
              {[2, 3, 6].map((m) => (
                <button
                  key={m}
                  data-testid={`lookback-${m}-btn`}
                  onClick={() => setLookback(m)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${lookback === m ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
                >{m}M</button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="exclude-oneoff-toggle"
                checked={excludeOneoff}
                onChange={(e) => setExcludeOneoff(e.target.checked)}
                className="accent-spidey-red h-3.5 w-3.5"
              />
              Exclude one-off categories
              {oneoffCount > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                  {oneoffCount}
                </span>
              )}
            </label>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="manage-exclusions-btn"
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors sm:ml-auto"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M6 12h12M10 18h4" />
                  </svg>
                  Ignored
                  {pinnedExcluded.size > 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-spidey-red text-white">
                      {pinnedExcluded.size}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="end" data-testid="exclusions-popover">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="font-display text-sm font-bold text-slate-900 dark:text-slate-100">Ignore in projection</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Pinned categories are never counted in next month's forecast.</div>
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {allKnownCategories.length === 0 ? (
                    <div className="text-xs text-slate-500 dark:text-slate-400 p-3 text-center">No categories yet.</div>
                  ) : (
                    allKnownCategories.map((c) => {
                      const isPinned = pinnedExcluded.has(c);
                      return (
                        <label
                          key={c}
                          data-testid={`exclusion-option-${c}`}
                          className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm"
                        >
                          <Checkbox
                            checked={isPinned}
                            disabled={savingExclusion}
                            onCheckedChange={() => togglePinnedCategory(c)}
                            className="data-[state=checked]:bg-spidey-red data-[state=checked]:border-spidey-red"
                          />
                          <span className={`flex-1 font-medium ${isPinned ? "text-slate-400 dark:text-slate-500 line-through" : "text-slate-700 dark:text-slate-200"}`}>
                            {c}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {pinnedExcluded.size > 0 && (
            <div className="mb-4 flex flex-wrap gap-1.5" data-testid="pinned-chips">
              {Array.from(pinnedExcluded).map((c) => {
                const found = pinnedCategoriesData.find(x => x.category === c);
                return (
                  <button
                    key={c}
                    data-testid={`pinned-chip-${c}`}
                    onClick={() => togglePinnedCategory(c)}
                    disabled={savingExclusion}
                    title={found ? `Would have added ${fmtMoney(found.amount, cur)} — click to un-ignore` : "Click to un-ignore"}
                    className="group flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 transition-colors"
                  >
                    <span className="line-through">{c}</span>
                    <span className="text-slate-400 group-hover:text-rose-500">×</span>
                  </button>
                );
              })}
            </div>
          )}

          {monthsAnalyzed === 0 || projTotal === 0 ? (
            <div className="text-sm text-slate-500 dark:text-slate-400 py-16 text-center">
              Not enough history yet. Log expenses across the last couple of months to unlock projections.
            </div>
          ) : (
            <>
              <div className={`rounded-2xl p-5 mb-4 border ${overBudget ? "bg-gradient-to-br from-rose-500/10 to-rose-500/5 dark:from-rose-500/20 dark:to-transparent border-rose-500/30" : "bg-gradient-to-br from-spidey-red/10 to-spidey-red/5 dark:from-spidey-red/20 dark:to-transparent border-spidey-red/20"}`}>
                <div className={`text-[11px] tracking-widest uppercase font-bold ${overBudget ? "text-rose-600" : "text-spidey-red"}`}>Projected Spend</div>
                <div
                  data-testid="projection-total"
                  className="font-display text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 tabular mt-1 break-words"
                >
                  {fmtMoney(projTotal, cur)}
                </div>
                {pinnedSavings > 0 && (
                  <div
                    data-testid="insight-nudge"
                    className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400"
                  >
                    <span className="text-sm leading-none">✦</span>
                    <span>
                      You'd save ~<span className="tabular">{fmtMoney(pinnedSavings, cur)}</span>/mo by ignoring{" "}
                      {pinnedCategoriesData.map((p, i) => (
                        <React.Fragment key={p.category}>
                          {i > 0 && (i === pinnedCategoriesData.length - 1 ? " and " : ", ")}
                          <span className="font-bold">{p.category}</span>
                        </React.Fragment>
                      ))}
                      .
                    </span>
                  </div>
                )}
                {monthlyBudget > 0 && (
                  <div className="mt-3" data-testid="budget-warning-block">
                    <div className="flex justify-between items-center text-xs mb-1.5">
                      <span className="text-slate-600 dark:text-slate-400">
                        Monthly budget: <span className="tabular font-semibold text-slate-800 dark:text-slate-200">{fmtMoney(monthlyBudget, cur)}</span>
                      </span>
                      <span className={`tabular font-bold ${overBudget ? "text-rose-600" : "text-emerald-600"}`}>
                        {budgetPct}%
                      </span>
                    </div>
                    <div className="relative h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${overBudget ? "bg-rose-500" : "bg-emerald-500"}`}
                        style={{ width: `${budgetPct}%` }}
                      />
                      {/* Budget line marker at 100% */}
                      <div className="absolute top-0 bottom-0 w-0.5 bg-slate-900 dark:bg-slate-100" style={{ left: "100%", transform: "translateX(-1px)" }} />
                    </div>
                    {overBudget ? (
                      <div
                        data-testid="over-budget-warning"
                        className="mt-2.5 flex items-start gap-2 text-xs font-semibold text-rose-700 dark:text-rose-400"
                      >
                        <span className="text-sm leading-none">⚠</span>
                        <span>
                          Heads up — next month is projected to swing {fmtMoney(overBy, cur)} past your monthly budget.
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-emerald-700 dark:text-emerald-400 font-semibold">
                        On track — projected under budget by {fmtMoney(monthlyBudget - projTotal, cur)}.
                      </div>
                    )}
                  </div>
                )}
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                  {monthlyTotals.map((m, i) => (
                    <span key={m.month_offset}>
                      {i > 0 && " · "}
                      {monthLabels[m.month_offset] || `M-${m.month_offset}`}: {fmtMoney(m.total, cur)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1">
                {projCats.slice(0, 8).map((c, i) => {
                  const p = projTotal > 0 ? Math.round((c.amount / projTotal) * 100) : 0;
                  return (
                    <div key={c.category} data-testid={`projection-row-${c.category}`}>
                      <div className="flex justify-between text-sm">
                        <div className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
                          <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                          />
                          {c.category}
                          {c.is_oneoff && (
                            <span
                              title="Only appeared in one month of the window"
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                            >
                              ONE-OFF
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Sparkline
                            values={c.trend || []}
                            color={PIE_COLORS[i % PIE_COLORS.length]}
                            width={56}
                            height={18}
                          />
                          <div className="tabular text-slate-500 dark:text-slate-400">{fmtMoney(c.amount, cur)}</div>
                          <button
                            data-testid={`ignore-row-${c.category}`}
                            onClick={() => togglePinnedCategory(c.category)}
                            disabled={savingExclusion}
                            title={`Ignore "${c.category}" in projections`}
                            className="text-slate-300 hover:text-rose-500 dark:text-slate-600 dark:hover:text-rose-400 text-sm leading-none w-5 h-5 rounded-full hover:bg-rose-50 dark:hover:bg-rose-500/10 flex items-center justify-center transition-colors"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <div className="h-1.5 mt-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${p}%`, background: PIE_COLORS[i % PIE_COLORS.length] }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {[
            { l: "Total Income", v: summary?.total_income || 0, c: "emerald" },
            { l: "Total Expense", v: summary?.total_expense || 0, c: "rose" },
            { l: summary?.is_current_month === false ? `${summary?.month_label || "Selected"} In` : "This Month In", v: summary?.month_income || 0, c: "emerald" },
            { l: summary?.is_current_month === false ? `${summary?.month_label || "Selected"} Out` : "This Month Out", v: summary?.month_expense || 0, c: "rose" },
          ].map((k) => (
            <div key={k.l} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-4 sm:p-5">
              <div className="text-[10px] sm:text-[11px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400 truncate">{k.l}</div>
              <div className={`font-display text-xl sm:text-2xl font-bold mt-2 tabular break-words ${k.c === "emerald" ? "text-emerald-600" : "text-rose-600"}`}>{fmtMoney(k.v, cur)}</div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
